import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import classNames from 'classnames';

import IconHeart2 from 'components/icons/iconHeart2';
import { PANTONE_FABRIC_CONFIG, PDP_NAMED_FILTERS } from 'commons/constants';
import { productFilterTypes } from 'types/PDPTypes';
import FlyoutContainer from 'components/consumer/FlyoutContainer';
import FlatButtonFilters from 'components/consumer/FlatButtonFilters';
import flyoutConfig from './flyoutConfig';

const { pantoneFilterLabel, pantoneFilterSlug } = PANTONE_FABRIC_CONFIG;

const PDPUpholsteryFiltersUI = ({
  className = '',
  clearAllFiltersButton,
  fabricFavInfoToShow,
  filterCounts = {},
  hideFlyout = false,
  isClearanceFabricAvailable = false,
  loading = false,
  selectedItem,
  setSelectedItem,
  variant,
}) => {
  const [flyOutContent, setFlyOutContent] = useState({});
  const [isFlyOutVisible, setIsFlyOutVisible] = useState(false);

  const containerRef = useRef(null);

  const items = useMemo(() => {
    const iconHeart = (
      <span className="flex items-center justify-center [&_svg]:w-[1.1rem] [&_svg]:h-[1.1rem] [&_svg_path]:fill-[#d86262] [&_svg_path]:stroke-[#d86262]">
        <IconHeart2 />
        &nbsp;
        {`(${filterCounts[PDP_NAMED_FILTERS.FAVORITES]})`}
      </span>
    );

    const updatedItems = [
      { label: iconHeart, id: PDP_NAMED_FILTERS.FAVORITES },
      { label: 'Velvets', id: PDP_NAMED_FILTERS.VELVET },
      { label: 'Pet-friendly', id: PDP_NAMED_FILTERS.PETFRIENDLY },
      { label: 'Safeguard', id: PDP_NAMED_FILTERS.SAFEGUARD },
      { label: 'Sustainable', id: PDP_NAMED_FILTERS.SUSTAINABLE },
      { label: 'GREENGUARD Gold', id: PDP_NAMED_FILTERS.GREENGUARD },
      { label: pantoneFilterLabel, id: PDP_NAMED_FILTERS.PANTONE },
    ];

    if (isClearanceFabricAvailable) {
      updatedItems.push({
        label: 'Clearance',
        id: PDP_NAMED_FILTERS.CLEARANCE,
      });
    }

    return updatedItems;
  }, [filterCounts, isClearanceFabricAvailable]);

  const isLoading = loading || isEmpty(filterCounts);

  const toggleFlyOut = useCallback(
    id => {
      if (hideFlyout || isLoading) {
        return;
      }

      if (id === pantoneFilterSlug) {
        setFlyOutContent({
          top: 0,
          ...flyoutConfig[pantoneFilterSlug],
        });
        setIsFlyOutVisible(true);
      } else if (id === 'pet-friendly') {
        setFlyOutContent({
          top: 0,
          ...flyoutConfig['pet-friendly'],
        });
        setIsFlyOutVisible(true);
      } else if (id === 'safeguard') {
        setFlyOutContent({
          top: 0,
          ...flyoutConfig.safeguard,
        });
        setIsFlyOutVisible(true);
      } else if (id === 'greenguard-gold') {
        setFlyOutContent({
          top: 0,
          ...flyoutConfig['greenguard-gold'],
        });
        setIsFlyOutVisible(true);
      }
    },
    [hideFlyout, isLoading]
  );

  const onClose = useCallback(() => setIsFlyOutVisible(false), []);

  const finalItems = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return items.filter(filterItem => {
      // keep favorites filter even if count is 0.
      if (
        filterCounts[filterItem.id] ||
        (filterItem.id === PDP_NAMED_FILTERS.FAVORITES &&
          filterCounts[PDP_NAMED_FILTERS.FAVORITES] !== undefined)
      ) {
        return true;
      }

      if (filterItem.id === PDP_NAMED_FILTERS.CLEARANCE) {
        // only push this when its a clearance product
        return true;
      }

      return false;
    });
  }, [filterCounts, isLoading, items]);

  if (isLoading) {
    return <FlatButtonFilters loading />;
  }

  return (
    <div
      className={classNames({
        [className]: !!className,
        'max-[1023px]:overflow-x-scroll max-[1023px]:overflow-y-hidden max-[1023px]:ml-5 max-[1023px]:[scrollbar-width:none] max-[1023px]:[-ms-overflow-style:none] max-[1023px]:[transition:margin_.5s]':
          variant === 'PDPSelectUpholstery',
      })}
      ref={containerRef}
    >
      {!!isFlyOutVisible && !hideFlyout && (
        <FlyoutContainer
          top={flyOutContent.top}
          imageUrl={flyOutContent.imageUrl}
          videoObj={flyOutContent.videoObj}
          content={flyOutContent.content}
          title={flyOutContent.title}
          onClose={onClose}
          isOpen={isFlyOutVisible}
        />
      )}

      <FlatButtonFilters
        customFinalButton={
          <div className="hidden sm:flex flex-1 justify-end shrink-[5]">
            {clearAllFiltersButton}
          </div>
        }
        items={finalItems}
        isSelectedButtonFilled
        onMouseEnter={id => {
          toggleFlyOut(id);
        }}
        onMouseLeave={onClose}
        selectedItem={selectedItem}
        setSelectedItem={id => {
          setSelectedItem(id);
        }}
        variant={variant}
      />

      {!!fabricFavInfoToShow && (
        <div className="text-xs font-bold max-[1023px]:pl-[22px]">
          {fabricFavInfoToShow}
        </div>
      )}
    </div>
  );
};

PDPUpholsteryFiltersUI.propTypes = {
  className: PropTypes.string,
  fabricFavInfoToShow: PropTypes.string,
  filterCounts: productFilterTypes,
  hideFlyout: PropTypes.bool,
  isClearanceFabricAvailable: PropTypes.bool,
  loading: PropTypes.bool,
  selectedItem: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  setSelectedItem: PropTypes.func,
  variant: PropTypes.oneOf(['PDPSelectUpholstery']),
};

export default PDPUpholsteryFiltersUI;
